import type {
  ObjectToolbarConfig,
  RegisterToolbarConfigArgs,
} from '@/blocks/object-toolbar';

/**
 * Assemble the object-toolbar config for the Oversight assessments register/list
 * page (CUS-164). Register toolbars are type-level: no `parentObject`
 * (permissions resolve against the type) and no `save` (a list page has no
 * form). The register's multi-format export (CSV / PDF / custom PDF) is surfaced
 * as the toolbar's "Download" group — one action per `ExportOption` returned by
 * `useExportOptions`, the same source that drives the legacy `ExportButton`. The
 * create button is surfaced as the toolbar's primary action, gated on
 * `insert:compliance_monitoring_assessment`.
 *
 * Note (parity): unlike the Actions register, the legacy Oversight create
 * `<Permission>` does NOT set `canHaveAccessAsContributor`, so this builder
 * omits it too — keeping the flag-on toolbar's create visibility identical to
 * the flag-off button.
 *
 * Takes the shared {@link RegisterToolbarConfigArgs} (see
 * `blocks/object-toolbar/types.ts`) — the input shape every module's register
 * builder uses across the CUS-136 fanout. Pure so it can be unit-tested without
 * rendering the page.
 */
export const buildOversightAssessmentRegisterToolbarConfig = ({
  title,
  counter,
  exportOptions,
  create,
  editRibbon,
}: RegisterToolbarConfigArgs): ObjectToolbarConfig => ({
  header: { title, counter },
  actions: {
    ...(create
      ? {
          primary: [
            {
              id: 'create',
              label: create.label,
              iconName: 'plus',
              onClick: create.onClick,
              permission: 'insert:compliance_monitoring_assessment',
            },
          ],
        }
      : {}),
    ...(editRibbon
      ? {
          overflow: [
            {
              id: 'edit',
              label: editRibbon.groupLabel,
              actions: [
                {
                  id: 'edit-ribbon',
                  label: editRibbon.label,
                  iconName: 'filter-lines',
                  onClick: editRibbon.onClick,
                },
              ],
            },
          ],
        }
      : {}),
    export: [
      {
        id: 'export',
        actions: exportOptions.map((option) => ({
          id: option.id,
          label: option.text,
          iconName: 'download-01',
          onClick: option.exportFunction,
          disabled: option.disabled,
        })),
      },
    ],
  },
});
